/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useMemo, useReducer, useLayoutEffect } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Plus, Trash2, Globe, CheckCircle2, AlertTriangle, Copy, Zap, Loader2, ArrowRight,
  Shield, ChevronDown, ChevronRight, Activity, XCircle, Radar, Mail,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_HOST = (BACKEND_URL || '').replace(/^https?:\/\//, '');
// Production host (where real customers pay) is derived from the preview host
// pattern: <app>.preview.emergentagent.com → <app>.emergent.host. This is what
// the customer's domain MUST point to (CNAME target) for checkout to work.
const PROD_HOST = BACKEND_HOST.includes('.preview.emergentagent.com')
  ? BACKEND_HOST.replace('.preview.emergentagent.com', '.emergent.host')
  : BACKEND_HOST.replace('.emergentagent.com', '.emergent.host');

function CopyableLine({ value, testId }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success('Copié'); } catch { toast.error('Impossible de copier'); }
  };
  return (
    <div className="flex items-center justify-between gap-2 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
      <code className="font-mono text-xs text-zinc-700 truncate" data-testid={testId}>{value}</code>
      <button type="button" onClick={copy} className="text-xs text-zinc-500 hover:text-zinc-700 shrink-0 flex items-center gap-1">
        <Copy className="h-3 w-3" /> Copier
      </button>
    </div>
  );
}

const STATUS_STYLE = {
  live: { ring: 'border-emerald-300 bg-emerald-50', text: 'text-emerald-800', Icon: CheckCircle2, badge: 'bg-emerald-100 text-emerald-700 border-emerald-300', tag: 'ACTIF' },
  ssl_error: { ring: 'border-amber-300 bg-amber-50', text: 'text-amber-800', Icon: AlertTriangle, badge: 'bg-amber-100 text-amber-700 border-amber-300', tag: 'SSL EN ATTENTE' },
  dns_ok_https_fail: { ring: 'border-amber-300 bg-amber-50', text: 'text-amber-800', Icon: AlertTriangle, badge: 'bg-amber-100 text-amber-700 border-amber-300', tag: 'HÔTE NON ACCEPTÉ' },
  dns_missing: { ring: 'border-red-300 bg-red-50', text: 'text-red-800', Icon: XCircle, badge: 'bg-red-100 text-red-700 border-red-300', tag: 'DNS MANQUANT' },
  unknown: { ring: 'border-zinc-300 bg-zinc-50', text: 'text-zinc-700', Icon: Radar, badge: 'bg-zinc-100 text-zinc-600 border-zinc-300', tag: 'INDÉTERMINÉ' },
};

/** 1-click deep activation diagnostic for any checkout host. */
function ActivationTester({ storeId, host, testIdPrefix }) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (!host) return;
    setLoading(true);
    setRes(null);
    try {
      const r = await api.get(`/stores/${storeId}/diagnose-domain`, { params: { host } });
      setRes(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du diagnostic');
    } finally {
      setLoading(false);
    }
  };
  const st = res ? (STATUS_STYLE[res.status] || STATUS_STYLE.unknown) : null;
  return (
    <div>
      <Button
        onClick={run}
        disabled={loading || !host}
        variant="outline"
        className="rounded-md h-9 text-xs"
        data-testid={`${testIdPrefix}-test-button`}
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Activity className="h-3.5 w-3.5 mr-1.5" />}
        Tester l'activation
      </Button>

      {res && st && (
        <div className={`mt-3 border rounded-md p-4 ${st.ring}`} data-testid={`${testIdPrefix}-result`}>
          <div className="flex items-start gap-2">
            <st.Icon className={`h-4 w-4 mt-0.5 shrink-0 ${st.text}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono border ${st.badge}`} data-testid={`${testIdPrefix}-status`}>{st.tag}</span>
                <code className="font-mono text-xs text-zinc-600 truncate">{res.host}</code>
              </div>
              <p className={`text-sm mt-1.5 ${st.text}`}>{res.label}</p>

              {/* Layered detail */}
              <div className="mt-3 grid sm:grid-cols-3 gap-2 text-[11px]">
                <div className="bg-white/70 border border-white rounded px-2 py-1.5">
                  <div className="text-zinc-400 uppercase tracking-wide">DNS</div>
                  <div className="font-semibold">{res.dns_resolves ? '✅ résolu' : '❌ non résolu'}</div>
                </div>
                <div className="bg-white/70 border border-white rounded px-2 py-1.5">
                  <div className="text-zinc-400 uppercase tracking-wide">HTTPS</div>
                  <div className="font-semibold">
                    {res.https_reachable ? `✅ ${res.https_status}` : (res.ssl_ok === false ? '🔒 SSL KO' : '❌ injoignable')}
                  </div>
                </div>
                <div className="bg-white/70 border border-white rounded px-2 py-1.5">
                  <div className="text-zinc-400 uppercase tracking-wide">Cible = apex ?</div>
                  <div className="font-semibold">
                    {res.platform_domain ? (res.matches_apex ? '✅ identique' : '⚠️ différent') : '—'}
                  </div>
                </div>
              </div>

              {res.records?.length > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="text-[10px] text-zinc-400 uppercase">Enregistrements détectés</div>
                  {res.records.map((r, i) => (
                    <code key={i} className="block font-mono text-[11px] bg-white/70 rounded px-2 py-1">
                      <span className="text-zinc-400">{r.type}</span> → {r.value}
                    </code>
                  ))}
                </div>
              )}
              {res.https_error && (
                <div className="mt-2 text-[11px] text-zinc-500 font-mono">↳ {res.https_error}</div>
              )}

              {/* Actionable fix per status */}
              {(res.status === 'dns_ok_https_fail' || res.status === 'ssl_error') && (
                <div className="mt-3 text-[11px] text-zinc-700 bg-white/70 border border-amber-200 rounded p-2.5">
                  Le DNS pointe bien, mais le déploiement ne sert pas (encore) cet hôte.
                  Vérifiez que le <strong>domaine wildcard</strong> (ou ce domaine exact) est ajouté côté
                  déploiement. Pour les sous-domaines wildcard, si le problème persiste, écrivez à{' '}
                  <a href="mailto:support@emergent.sh" className="underline inline-flex items-center gap-0.5">
                    <Mail className="h-3 w-3" /> support@emergent.sh
                  </a>.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DomainRow({ domain, storeId, onRemove }) {
  return (
    <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid={`domain-row-${domain}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Globe className="h-4 w-4 text-zinc-400 shrink-0" />
          <code className="font-mono text-sm truncate">{domain}</code>
        </div>
        <Button size="sm" variant="ghost" onClick={onRemove} className="rounded-md h-8 w-8 p-0 text-red-600 hover:text-red-700">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <ActivationTester storeId={storeId} host={domain} testIdPrefix={`domain-${domain}`} />
    </div>
  );
}

const REGISTRAR_TIPS = [
  { name: 'OVH', steps: 'Domaines → Zone DNS → Ajouter une entrée → CNAME' },
  { name: 'Cloudflare', steps: 'DNS → Records → Add → CNAME → désactive le proxy (DNS only)' },
  { name: 'Gandi', steps: 'Nom de domaine → DNS Records → Add → CNAME' },
  { name: 'Namecheap', steps: 'Advanced DNS → Add New Record → CNAME Record' },
  { name: 'GoDaddy', steps: 'DNS Management → Records → Add → CNAME' },
  { name: 'Google Domains', steps: 'DNS → Manage custom records → Create → CNAME' },
];

export default function Hosting() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [form, updateForm] = useReducer((s, patch) => ({ ...s, ...patch }), { slug: '', subdomain: '', custom_domains: [] });
  const [newDomain, setNewDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Global settings (platform domain)
  const [settings, setSettings] = useState({ vitrine_catalog_url: '', vitrine_api_key: '', platform_domain: '' });
  const [platformInput, setPlatformInput] = useState('');
  const [savingPlatform, setSavingPlatform] = useState(false);

  const stableStores = useMemo(() => stores, [stores]);
  const syncForm = (sid, list) => {
    if (!sid) return;
    const s = list.find((x) => x.id === sid);
    if (s) updateForm({ slug: s.slug || '', subdomain: s.subdomain || '', custom_domains: s.custom_domains || [] });
  };

  const load = async () => {
    const [r, sr] = await Promise.all([api.get('/stores'), api.get('/settings')]);
    setStores(r.data);
    setSettings(sr.data);
    setPlatformInput(sr.data.platform_domain || '');
    if (r.data.length && !storeId) setStoreId(r.data[0].id);
  };
  useLayoutEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => { syncForm(storeId, stableStores); }, [storeId, stableStores]); // eslint-disable-line react-hooks/exhaustive-deps

  const platformDomain = settings.platform_domain || '';

  const savePlatform = async () => {
    const v = platformInput.trim().toLowerCase().replace(/^https?:\/\//, '').split('/')[0].replace(/\.$/, '');
    if (v && !/^[a-z0-9.-]+\.[a-z]{2,}$/.test(v)) {
      toast.error('Format invalide (ex: checkout-commande.live)');
      return;
    }
    setSavingPlatform(true);
    try {
      // Preserve other settings; only update platform_domain.
      await api.put('/settings', { ...settings, platform_domain: v });
      toast.success('Domaine de la plateforme enregistré');
      setSettings((s) => ({ ...s, platform_domain: v }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSavingPlatform(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const s = stores.find((x) => x.id === storeId);
      if (!s) return;
      const payload = { ...s, slug: form.slug, subdomain: form.subdomain, custom_domains: form.custom_domains };
      delete payload.id;
      delete payload.created_at;
      delete payload.shopify_connected;
      await api.put(`/stores/${storeId}`, payload);
      toast.success('Configuration enregistrée');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const addDomain = () => {
    const d = newDomain.trim().toLowerCase().replace(/^https?:\/\//, '').split('/')[0];
    if (!d) return;
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(d)) {
      toast.error('Format de domaine invalide (ex: pay.boutique.com)');
      return;
    }
    if (form.custom_domains.includes(d)) { toast.error('Domaine déjà ajouté'); return; }
    updateForm({ custom_domains: [...form.custom_domains, d] });
    setNewDomain('');
  };
  const removeDomain = (d) => updateForm({ custom_domains: form.custom_domains.filter((x) => x !== d) });

  const subdomainFull = form.subdomain && platformDomain ? `${form.subdomain}.${platformDomain}` : '';
  const slugUrl = form.slug ? `${BACKEND_URL}/c/${form.slug}` : '';

  const snippetUrl = useMemo(() => {
    if (!storeId) return '';
    return `${BACKEND_URL}/api/snippet.js?v=2&store=${storeId}`;
  }, [storeId]);

  const storeBadge = (s) => {
    if ((s.custom_domains || []).length > 0) return { c: 'text-emerald-600', t: 'domaine perso' };
    if (s.subdomain && platformDomain) return { c: 'text-blue-600', t: 'sous-domaine' };
    return { c: 'text-amber-500', t: 'aucun' };
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl">
      <div className="mb-8">
        <div className="overline mb-2">— Branding & Infrastructure</div>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Domaines de paiement</h1>
        <p className="text-zinc-600 mt-2">
          Donnez à chaque boutique <strong>son propre domaine de paiement</strong>. Deux approches : un{' '}
          <strong>sous-domaine</strong> de votre plateforme (ex: <code className="font-mono text-sm">boutiqueA.{platformDomain || 'votre-domaine.com'}</code>),
          ou un <strong>domaine personnalisé</strong> appartenant au marchand (ex: <code className="font-mono text-sm">pay.maboutique.com</code>).
        </p>
      </div>

      {/* Platform domain config */}
      <Card className="card-flat p-6 mb-6" data-testid="platform-domain-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-blue-600 text-white flex items-center justify-center shrink-0">
            <Globe className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-bold tracking-tight">Domaine de la plateforme</h2>
            <p className="text-sm text-zinc-600 mt-1">
              Le domaine racine sur lequel sont basés les <strong>sous-domaines</strong> de vos boutiques.
              Requis pour activer les sous-domaines type <code className="font-mono">boutique.{platformInput || 'votre-domaine.com'}</code>.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Input
            value={platformInput}
            onChange={(e) => setPlatformInput(e.target.value)}
            placeholder="checkout-commande.live"
            data-testid="platform-domain-input"
            className="h-11 rounded-md font-mono flex-1 max-w-md"
            onKeyDown={(e) => e.key === 'Enter' && savePlatform()}
          />
          <Button onClick={savePlatform} disabled={savingPlatform} className="btn-primary rounded-md h-11" data-testid="platform-domain-save">
            {savingPlatform ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        </div>
        {platformDomain && (
          <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" /> Configuré : <code className="font-mono">{platformDomain}</code>
          </p>
        )}
      </Card>

      {/* Wildcard DNS instructions for subdomains */}
      <Card className="p-6 mb-6 !bg-gradient-to-br from-zinc-900 to-zinc-800 text-white border-0 shadow-lg rounded-md">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-md bg-blue-500 flex items-center justify-center shrink-0">
            <Shield className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold mb-2">Activer les sous-domaines (configuration unique)</h2>
            <p className="text-sm text-zinc-300 mb-4">
              Une seule fois, créez un enregistrement DNS <strong>wildcard</strong> pour router tous vos sous-domaines vers la plateforme :
            </p>
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              <div className="bg-white/10 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Type</div>
                <code className="font-mono text-sm">CNAME</code>
              </div>
              <div className="bg-white/10 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Nom / Host</div>
                <code className="font-mono text-sm">*</code>
              </div>
              <div className="bg-white/10 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Cible</div>
                <code className="font-mono text-xs break-all">{PROD_HOST}</code>
              </div>
            </div>
            <ul className="text-xs text-zinc-300 space-y-1.5">
              <li className="flex gap-2"><span className="text-blue-400">1.</span> Chez votre registrar, créez le CNAME <code className="font-mono">*</code> ci-dessus dans la zone de <code className="font-mono">{platformDomain || 'votre-domaine'}</code>.</li>
              <li className="flex gap-2"><span className="text-blue-400">2.</span> Ajoutez le domaine <code className="font-mono">*.{platformDomain || 'votre-domaine'}</code> dans les domaines personnalisés de votre déploiement Emergent.</li>
              <li className="flex gap-2"><span className="text-blue-400">3.</span> Attendez la propagation + le provisionnement SSL (jusqu'à 24h), puis utilisez « Tester l'activation » sur chaque boutique.</li>
            </ul>
            <div className="mt-3 text-xs text-zinc-400 bg-white/5 rounded-md p-2.5">
              ⚠️ Si le wildcard ne s'active pas malgré DNS + déploiement OK, le support wildcard peut nécessiter une activation manuelle côté plateforme — écrivez à{' '}
              <a href="mailto:support@emergent.sh" className="underline text-white">support@emergent.sh</a> (joignez votre Job ID + le domaine).
            </div>
          </div>
        </div>
      </Card>

      {/* Store selector */}
      <Card className="card-flat p-6 mb-6">
        <Label className="overline mb-3 block">— Boutique à configurer</Label>
        <Select value={storeId} onValueChange={setStoreId}>
          <SelectTrigger data-testid="hosting-store-select" className="h-11 rounded-md max-w-md">
            <SelectValue placeholder="Choisir une boutique" />
          </SelectTrigger>
          <SelectContent>
            {stores.map((s) => {
              const b = storeBadge(s);
              return (
                <SelectItem key={s.id} value={s.id}>
                  <span className="flex items-center gap-2">{s.name}<span className={`text-xs ${b.c}`}>● {b.t}</span></span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </Card>

      {storeId && (
        <>
          {/* SUBDOMAIN — primary */}
          <Card className="card-flat p-6 mb-6" data-testid="subdomain-card">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-blue-600 text-white flex items-center justify-center shrink-0 font-bold">A</div>
              <div className="flex-1">
                <h2 className="font-display text-xl font-bold tracking-tight">Sous-domaine de la plateforme</h2>
                <p className="text-sm text-zinc-600 mt-1">
                  Le plus simple : aucun DNS à gérer par boutique (le wildcard fait tout). Choisissez juste un préfixe.
                </p>
              </div>
            </div>

            {!platformDomain ? (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-4">
                ⚠️ Configurez d'abord le <strong>domaine de la plateforme</strong> ci-dessus pour utiliser les sous-domaines.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1 mb-2">
                  <Input
                    value={form.subdomain}
                    onChange={(e) => updateForm({ subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="boutiquea"
                    data-testid="hosting-subdomain-input"
                    className="h-11 rounded-md font-mono max-w-xs"
                  />
                  <span className="font-mono text-sm text-zinc-500">.{platformDomain}</span>
                </div>
                {subdomainFull && (
                  <div className="text-xs text-zinc-500 mb-3">
                    URL de paiement : <code className="font-mono text-blue-700" data-testid="subdomain-full-url">https://{subdomainFull}</code>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button onClick={save} disabled={saving} data-testid="subdomain-save-button" className="btn-primary rounded-md">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Enregistrer le sous-domaine
                  </Button>
                  {subdomainFull && <ActivationTester storeId={storeId} host={subdomainFull} testIdPrefix="subdomain" />}
                </div>
              </>
            )}
          </Card>

          {/* CUSTOM DOMAIN */}
          <Card className="card-flat p-6 mb-6" data-testid="custom-domain-card">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-zinc-900 text-white flex items-center justify-center shrink-0 font-bold">B</div>
              <div className="flex-1">
                <h2 className="font-display text-xl font-bold tracking-tight">Domaine personnalisé du marchand</h2>
                <p className="text-sm text-zinc-600 mt-1">
                  Pour utiliser un domaine appartenant au marchand (ex:
                  <code className="font-mono mx-1 px-1.5 py-0.5 bg-zinc-100 rounded text-xs">pay.maboutique.com</code>).
                  Prioritaire sur le sous-domaine s'il est défini.
                </p>
              </div>
            </div>

            <div className="flex gap-2 mb-3">
              <Input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="pay.maboutique.com"
                data-testid="hosting-new-domain-input"
                className="h-11 rounded-md font-mono flex-1"
                onKeyDown={(e) => e.key === 'Enter' && addDomain()}
              />
              <Button onClick={addDomain} data-testid="hosting-add-domain-button" className="btn-primary rounded-md h-11">
                <Plus className="h-4 w-4 mr-1" /> Ajouter
              </Button>
            </div>

            {form.custom_domains.length === 0 ? (
              <div className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-md p-6 text-center">
                Aucun domaine personnalisé. Tapez le vôtre ci-dessus.
              </div>
            ) : (
              <div className="space-y-2">
                {form.custom_domains.map((d, i) => (
                  <div key={d} className="relative">
                    {i === 0 && <div className="absolute -left-1 top-3 bottom-3 w-0.5 bg-emerald-500"></div>}
                    <DomainRow domain={d} storeId={storeId} onRemove={() => removeDomain(d)} />
                    {i === 0 && (
                      <div className="text-[10px] text-emerald-600 font-mono uppercase mt-1 ml-1">↑ Domaine principal — utilisé pour les liens de paiement</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-4">
              <Button onClick={save} disabled={saving} data-testid="hosting-save-button" className="btn-primary rounded-md px-6">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Enregistrer
              </Button>
            </div>

            {form.custom_domains.length > 0 && (
              <div className="mt-5 pt-5 border-t border-zinc-100">
                <div className="overline mb-3">— DNS à créer chez le registrar du marchand</div>
                <div className="grid sm:grid-cols-3 gap-3 mb-3">
                  <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
                    <div className="overline mb-2">Type</div>
                    <CopyableLine value="CNAME" testId="dns-type" />
                  </div>
                  <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
                    <div className="overline mb-2">Nom / Host</div>
                    <CopyableLine value={form.custom_domains[0].split('.').slice(0, -2).join('.') || '@'} testId="dns-host" />
                  </div>
                  <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
                    <div className="overline mb-2">Cible</div>
                    <CopyableLine value={PROD_HOST} testId="dns-target" />
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-zinc-700">
                  <div className="font-semibold text-blue-900 mb-1.5">Tutoriel par registrar</div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                    {REGISTRAR_TIPS.map((t) => (
                      <div key={t.name}><span className="font-mono font-bold text-zinc-900">{t.name}</span> : {t.steps}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Snippet preview */}
          <Card className="card-flat p-6 mb-6">
            <div className="overline mb-2">— Snippet à coller dans Shopify (theme.liquid)</div>
            <CopyableLine value={`<script src="${snippetUrl}" data-store-id="${storeId}" defer></script>`} testId="hosting-snippet-preview" />
            <p className="text-xs text-zinc-500 mt-2 flex items-start gap-1.5">
              <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Ce snippet redirige automatiquement vers
              <code className="font-mono">
                {form.custom_domains[0]
                  ? ` https://${form.custom_domains[0]}/checkout`
                  : subdomainFull ? ` https://${subdomainFull}/checkout` : ' l\'URL de la plateforme'}
              </code>
            </p>
          </Card>

          {/* Advanced: slug */}
          <Card className="card-flat overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full p-5 flex items-center justify-between hover:bg-zinc-50 transition"
            >
              <div className="flex items-center gap-3">
                <Zap className="h-4 w-4 text-zinc-400" />
                <span className="font-semibold text-sm">Options avancées · URL de campagne (slug)</span>
              </div>
              {showAdvanced ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
            </button>
            {showAdvanced && (
              <div className="p-6 pt-2 border-t border-zinc-100 space-y-4">
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Slug de campagne</Label>
                  <Input
                    value={form.slug}
                    onChange={(e) => updateForm({ slug: e.target.value })}
                    placeholder="ma-boutique"
                    data-testid="hosting-slug-input"
                    className="h-10 rounded-md font-mono max-w-md"
                  />
                  {slugUrl && <div className="text-xs text-zinc-500 mt-2">URL : <code className="font-mono">{slugUrl}</code></div>}
                </div>
                <Button onClick={save} disabled={saving} className="btn-primary rounded-md">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Enregistrer
                </Button>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
